import { AfterViewInit, Component, computed, OnDestroy, signal, ViewChild } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { lastValueFrom, Subscription } from "rxjs";
import LocalizationConstants from "src/app/constants/localization-constants";
import CustomerSettings from "src/app/models/CustomerSettings";
import { ShippingAccount } from "src/app/models/ShippingAccount";
import { CompanyService } from "src/app/services/company.service";
import CustomerSettingsService from "src/app/services/customer-settings.service";
import { GlobalOverlayService } from "src/app/services/global-overlay.service";
import { ModalConfig, ModalService } from "src/app/services/modal.service";
import { ShippingAccountsService } from "src/app/services/shipping-accounts.service";

import { ConfirmationDialogData } from "../../common/confirmation-dialog/confirmation-dialog-modal.component";
import {
  CustomerSettingsEditModalComponent,
  CustomerSettingsEditModalComponentData,
} from "../customer-settings-edit-modal/customer-settings-edit-modal.component";

@Component({
  selector: "app-customers",
  templateUrl: "./customers.component.html",
  standalone: false,
})
export class CustomersComponent implements AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  readonly localizationConstants = LocalizationConstants;
  readonly faSpinner = faSpinner;
  private subscriptions: Subscription[] = [];

  availableShippingAccounts = signal<ShippingAccount[]>([]);
  shippingAccountsIdNameMap = computed<Record<string, string>>(() => {
    const mapping: Record<string, string> = {};
    this.availableShippingAccounts().forEach((x) => {
      mapping[x.shippingAccountId] = x.accountName;
    });
    return mapping;
  });

  isLoading = signal(true);
  dataSource = signal(new MatTableDataSource<CustomerSettings>([]));
  columnsToDisplay = [
    "companyId",
    "companyName",
    "shippingAccountId",
    "unitOfMeasure",
    "isShippingEnabled",
    "isTechCoverEnabled",
    "action1",
  ];

  constructor(
    private customerSettingsService: CustomerSettingsService,
    public companyService: CompanyService,
    private overlayService: GlobalOverlayService,
    public modalService: ModalService,
    private shippingAccountsService: ShippingAccountsService,
  ) {
    this.loadInitialDataAsync();
  }

  ngAfterViewInit() {
    this.dataSource.update((dataSource) => {
      dataSource.sort = this.sort;
      dataSource.paginator = this.paginator;
      return dataSource;
    });
  }

  private async loadInitialDataAsync() {
    this.isLoading.set(true);
    await Promise.all([this.loadCustomerSettingsAsync(), this.loadShippingAccountsAsync()]);
    this.isLoading.set(false);
  }

  private async loadShippingAccountsAsync(): Promise<void> {
    this.availableShippingAccounts.set(await this.shippingAccountsService.getAllAsync());
  }

  async openCreateModal(settings?: CustomerSettings) {
    const modalConfig = new ModalConfig<CustomerSettingsEditModalComponentData>();
    modalConfig.width = "700px";
    modalConfig.maxHeight = "800px";
    modalConfig.autoFocus = "first-heading";
    modalConfig.data = {
      editCompanySettings: settings,
      companySettingsList: this.dataSource().data,
      availableShippingAccounts: this.availableShippingAccounts(),
    };

    const result = await lastValueFrom(
      this.modalService.openModal(CustomerSettingsEditModalComponent, modalConfig),
    );
    if (!result) return;
    this.dataSource.update((dataSource) => {
      const found = dataSource.data.find((c) => c.companyId === result.companyId);
      if (found) {
        Object.assign(found, result);
      } else {
        dataSource.data = [...dataSource.data, result];
      }
      return dataSource;
    });
  }

  private async loadCustomerSettingsAsync() {
    const settings = await this.customerSettingsService.getAllAsync();
    this.dataSource.update((dataSource) => {
      dataSource.data = settings;
      return dataSource;
    });
  }

  public async toggleShipping(settings: CustomerSettings) {
    if (!settings.areShippingSettingsValid()) {
      const config = new ConfirmationDialogData(
        this.localizationConstants.CUSTOMER_SETTINGS_INVALID_SHIPPING_DETAILS_TITLE,
        [this.localizationConstants.CUSTOMER_SETTINGS_INVALID_SHIPPING_DETAILS],
        true,
      );
      this.modalService.openConfirmationModal(config);
      // Checkbox not updated in UI without setTimeout
      setTimeout(() => {
        settings.isShippingEnabled = false;
      });
      return;
    }
    this.updateCustomerSettingsAsync(settings);
  }

  private async updateCustomerSettingsAsync(settings: CustomerSettings) {
    this.overlayService.show();
    try {
      await this.customerSettingsService.updateAsync(settings);
    } finally {
      this.overlayService.close();
    }
  }

  public toggleTechCover(settings: CustomerSettings) {
    this.updateCustomerSettingsAsync(settings);
  }

  public editSettings(settings: CustomerSettings) {
    this.openCreateModal(settings);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x.unsubscribe());
  }
}
